import * as React from 'react';
import * as _ from 'underscore';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { IGroupPlaceScope } from '../../../ts/controllers/GroupPlaceCtrl';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { IGroupInfo } from '../../../ts/models/IGroupInfo';
import { Groups } from '../../../ts/resources/Groups';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { ServiceLocator } from '../../ServiceLocator';
import { GroupSubspaceName, SpaceInfo } from '../../state/SpaceInfo';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { RouterError } from '../RouterHelpers/RouterError';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

export interface GroupSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly subspaceName: GroupSubspaceName;
  readonly groupId: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
}

export interface GroupSpaceProviderStateProps {}

export interface GroupSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
}

interface GroupSpaceProviderPropsFromAngular {
  readonly groups: Groups;
}

export type GroupSpaceProviderNonAngularProps = GroupSpaceProviderOwnProps & GroupSpaceProviderStateProps & GroupSpaceProviderDispatchProps;

export type GroupSpaceProviderContainerProps = GroupSpaceProviderOwnProps & GroupSpaceProviderStateProps & GroupSpaceProviderDispatchProps & GroupSpaceProviderPropsFromAngular;

export interface GroupSpaceProviderState {
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly groupInfo: IGroupInfo;
}

const InitialState: GroupSpaceProviderState = {
  renderChildren: false,
  hasError: false,
  groupInfo: undefined
};

const template = `<div ng-controller="GroupPlaceCtrl" ng-include="'group.html'"></div>`;

export class GroupSpaceProviderContainer extends React.PureComponent<GroupSpaceProviderContainerProps, GroupSpaceProviderState> {

  private spaceScope: IGroupPlaceScope = null;
  private unmounted: boolean = false;

  public constructor(props: GroupSpaceProviderContainerProps) {
    super(props);
    this.state = InitialState;
  }

  public render() {
    if (this.state.renderChildren && this.haveAllDataNeededToRender()) {
      return this.renderSubSpace();
    } else if (this.state.hasError) {
      return <RouterError errorType={'group'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentDidMount(){
    this.getData();
  }

  public componentDidUpdate(prevProps: GroupSpaceProviderContainerProps) {
    if (this.changedGroup(prevProps)) {
      this.updateState(InitialState /* initial state for new group */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    this.props.enterSpace({
      spaceType: 'group',
      subspaceName: this.props.subspaceName,
      item: this.state.groupInfo
    } as SpaceInfo);

    if (this.state.renderChildren === false) {
      this.updateState({renderChildren: true}, () => {
        this.spaceScope.$apply();
      });
    } else if (prevProps.subspaceName !== this.props.subspaceName) {
      this.spaceScope.$apply();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private changedGroup(prevProps: GroupSpaceProviderProps) {
    return !_.isUndefined(prevProps.groupId)
      && !_.isUndefined(this.props.groupId)
      && prevProps.groupId !== this.props.groupId;
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.groupInfo);
  }

  private getData = async () => {
    try {
      const group = await this.props.groups.getById(this.props.groupId);
      this.updateState({groupInfo: group});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private updateState<K extends keyof GroupSpaceProviderState>(newState: Pick<GroupSpaceProviderState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private renderSubSpace = () => {
    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div>
        <AngularDirective
          template={template}
          angularContext={this.props.angularContext}
          setupScope={this.setupScope}
        />
      </div>
    );
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as IGroupPlaceScope;
    this.spaceScope = childScope;
    childScope.group = this.state.groupInfo;
    return childScope;
  }
}

export namespace GroupSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const groups = ServiceLocator.get<Groups>('Groups');
    return {
      groups,
    };
  }

  function selectPropsFromAngularServices(angularServices: GroupSpaceProviderPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<GroupSpaceProviderNonAngularProps, GroupSpaceProviderPropsFromAngular, GroupSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const GroupSpaceProviderAng: React.ComponentType<GroupSpaceProviderNonAngularProps> = GroupSpaceProviderAngularProvider.HOC(GroupSpaceProviderContainer);

const mapDispatchToProps: MapDispatchToProps<GroupSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
}, dispatch);

export const ConnectedGroupSpaceProviderContainer: React.ComponentClass<GroupSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(GroupSpaceProviderAng);

export type GroupSpaceProviderProps = GroupSpaceProviderOwnProps;
export function GroupSpaceProvider(props: GroupSpaceProviderProps) {
  return <ConnectedGroupSpaceProviderContainer {...props} />;
}
